import { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './services/AuthContext';
import { useComparison } from './services/ComparisonContext';
import { ConfigProvider } from './context/ConfigProvider'; // Import ConfigProvider
import { Helmet } from 'react-helmet-async';
import { Megaphone, X } from 'lucide-react';
import api from './services/api';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { CurrencyProvider } from './services/CurrencyContext';
import { AuthModalProvider } from './context/AuthModalContext';
import AuthModal from './components/AuthModal';

// Component Imports
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ComparisonFloatingBar from './components/ComparisonFloatingBar';
import ChatWidget from './components/ChatWidget';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';

// ... (Lazy loads remain same)
const HomePage = lazy(() => import('./pages/HomePage'));
const PropertyDetailPage = lazy(() => import('./pages/PropertyDetailPage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
const RentalListingPage = lazy(() => import('./pages/RentalListingPage'));
const CommercialPage = lazy(() => import('./pages/CommercialPage'));
const DeveloperProfilePage = lazy(() => import('./pages/DeveloperProfilePage'));
const CompoundDetailPage = lazy(() => import('./pages/CompoundDetailPage'));
const AreaProfilePage = lazy(() => import('./pages/AreaProfilePage'));
const SellPage = lazy(() => import('./pages/SellPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));


const ComparePage = lazy(() => import('./pages/ComparePage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));

const MyPropertiesPage = lazy(() => import('./pages/MyPropertiesPage'));
const InvestmentCenterPage = lazy(() => import('./pages/InvestmentCenterPage'));

const CheckEmailPage = lazy(() => import('./pages/CheckEmailPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const RequestPasswordResetPage = lazy(() => import('./pages/RequestPasswordResetPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));



import { CountryProvider, useCountry } from './context/CountryContext';

// --- Lazy Loads ---
const DevelopersListPage = lazy(() => import('./pages/DevelopersListPage'));
const AreasListPage = lazy(() => import('./pages/AreasListPage'));
const NewLaunchesPage = lazy(() => import('./pages/NewLaunchesPage'));
const OffersPage = lazy(() => import('./pages/OffersPage'));

const MoveNowPayLaterPage = lazy(() => import('./pages/MoveNowPayLaterPage'));
const AIMatchPage = lazy(() => import('./pages/AIMatchPage'));

const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PartnershipsPage = lazy(() => import('./pages/PartnershipsPage'));



// --- Helper Components ---
const PageLoader = () => (
  <div className="flex h-[80vh] w-full items-center justify-center bg-black">
    <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-500"></div>
  </div>
);

const BroadcastBanner = () => {
    const [message, setMessage] = useState('');
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        api.get('/api/v1/admin/public-settings').then(res => {
            if (Array.isArray(res.data)) {
                const announcement = res.data.find((s: any) => s.key === 'announcement_text');
                if (announcement && announcement.value) {
                    setMessage(announcement.value);
                    setVisible(true);
                }
            }
        }).catch(() => {});
    }, []);

    if (!visible || !message) return null;

    return (
        <div className="bg-[#334155] text-white px-4 py-2 flex items-center justify-center relative z-50 animate-in slide-in-from-top duration-500">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
                <Megaphone className="w-4 h-4 animate-pulse" />
                <span>{message}</span>
            </div>
            <button 
                onClick={() => setVisible(false)}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Close Announcement"
            >
                <X className="w-3 h-3" />
            </button>
        </div>
    );
};

const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const { isLoggedIn, isLoading } = useAuth();
  if (isLoading) return <PageLoader />;
  return isLoggedIn ? children : <Navigate to="/login" replace />;
};





const RootRedirect = () => {
    const { activeCountry } = useCountry();
    if (!activeCountry) return <PageLoader />;
    return <Navigate to={`/${activeCountry.code.toLowerCase()}`} replace />;
};



function AppLayout() {
  const location = useLocation();
  const { selectedProperties } = useComparison();
  
  // Routes where we want to hide the global Navbar and/or Footer (Path agnostic check)
  const isHidePath = (paths: string[]) => paths.some(p => location.pathname.includes(p));
  
  const hideGlobalLayout = isHidePath(['/move-now-pay-later', '/admin', '/ai-match']);
  const isSearchPage = isHidePath(['/search']);
  const searchParams = new URLSearchParams(location.search);
  const isMapView = isSearchPage && searchParams.get('view') === 'map';

  return (
    <div className={`flex flex-col min-h-screen font-sans transition-all duration-300 ${
      selectedProperties.length > 0 ? 'pb-28' : ''
    }`}>
      <BroadcastBanner />
      {!hideGlobalLayout && <Navbar />}
      <main className="flex-grow">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* --- Public Routes (Relative Paths) --- */}
            <Route path="" element={<HomePage />} />
            <Route path="search" element={<SearchResultsPage />} />
            <Route path="property/:id" element={<PropertyDetailPage />} />
            <Route path="compound/:id" element={<CompoundDetailPage />} />
            <Route path="area/:id" element={<AreaProfilePage />} />
            <Route path="developer/:id" element={<DeveloperProfilePage />} />
            <Route path="rent" element={<RentalListingPage />} />
            <Route path="commercial" element={<CommercialPage />} />
            <Route path="sell" element={<SellPage />} />
            <Route path="valuation" element={<SellPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="blog" element={<BlogPage />} />


            <Route path="compare" element={<ComparePage />} />


            {/* --- Public Pages (Consumer Facing) --- */}
            <Route path="developers" element={<DevelopersListPage />} />
            <Route path="areas" element={<AreasListPage />} />
            <Route path="launches" element={<NewLaunchesPage />} />
            <Route path="offers" element={<OffersPage />} />
            <Route path="investment" element={<InvestmentCenterPage />} />

            <Route path="move-now-pay-later" element={<MoveNowPayLaterPage />} />
            <Route path="ai-match" element={<AIMatchPage />} />

            <Route path="privacy" element={<PrivacyPage />} />
            <Route path="terms" element={<TermsPage />} />
            <Route path="partnerships" element={<PartnershipsPage />} />


            {/* --- Guest Auth Removed --- */}
            {/* Guests browse without accounts - signup triggered on inquiry/tour/save */}
            {/* <Route path="login" element={<PublicRoute><LoginPage /></PublicRoute>} /> */}
            {/* <Route path="register" element={<PublicRoute><RegisterPage /></PublicRoute>} /> */}
            <Route path="check-email" element={<CheckEmailPage />} />
            <Route path="verify-email" element={<VerifyEmailPage />} />
            <Route path="reset-password-request" element={<RequestPasswordResetPage />} />
            <Route path="reset-password" element={<ResetPasswordPage />} />

            {/* --- Protected Routes (Logged In Users) --- */}
            <Route path="profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="my-properties" element={<ProtectedRoute><MyPropertiesPage /></ProtectedRoute>} />

            <Route path="favorites" element={<Navigate to="../profile" replace />} />
            {/* Catch all INSIDE country scope -> Redirect to Country Home */}
            <Route path="*" element={<Navigate to="" />} />
          </Routes>
        </Suspense>
      </main>
      {!hideGlobalLayout && !isMapView && <Footer />}
      {!location.pathname.includes('/compare') && <ComparisonFloatingBar />}
      <ChatWidget />
      <AuthModal />
    </div>
  );
}


function App() {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || "YOUR_GOOGLE_CLIENT_ID_HERE"}>
      <AuthModalProvider>
          <ConfigProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <CountryProvider>
              <CurrencyProvider>
                <Helmet>
                  <title>Laqy Estate - Redefining Luxury Real Estate</title>
                  <meta name="description" content="Discover Egypt's most exclusive properties. Laqy Estate connects you with luxury compounds, developers, and premium listings." />
                  <meta property="og:title" content="Laqy Estate | Luxury Real Estate" />
                  <meta property="og:description" content="Your gateway to premium real estate in Egypt." />
                  <meta property="og:image" content="/og-default.png" />
                  <meta property="og:type" content="website" />
                </Helmet>
                <ErrorBoundary>
                  <ScrollToTop />
                  <Routes>
                      <Route path="/" element={<RootRedirect />} />
  


                      <Route path="/:countryCode/*" element={<AppLayout />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </ErrorBoundary>
              </CurrencyProvider>
            </CountryProvider>
          </Router>
          </ConfigProvider>
      </AuthModalProvider>
    </GoogleOAuthProvider>
  );
}

export default App;

